import React, { useState } from 'react';
import { Button, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DropzoneArea from '../DropzoneArea';
import CustomMonthDatePicker from '../../../Widgets/CustomDates/CustomMonthDatePicker/CustomMonthDatePicker';
import dayjs from 'dayjs';

const PriorityOpeningBalanceImportModal = ({
    open,
    onClose,
    previewPriorityOpeningBalance,
    commitPriorityOpeningBalance,
    direction,
    t
}) => {
    const [openingBalanceFile, setOpeningBalanceFile] = useState(null);
    const [openingBalanceEntries, setOpeningBalanceEntries] = useState([]);
    const [previewDone, setPreviewDone] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [parsedRows, setParsedRows] = useState(0);
    const [selectedDate, setSelectedDate] = useState(new Date(Date.UTC(new Date().getFullYear(), new Date().getMonth(), 1)));

    const handleFileDrop = files => {
        if (files?.length) setOpeningBalanceFile(files[0]);
    };

    const handlePreview = async () => {
        if (!openingBalanceFile) return;

        try {
            setProcessing(true);
            const month = selectedDate ? selectedDate.getUTCMonth() + 1 : null; // JavaScript months are 0-based
            const year = selectedDate ? selectedDate.getUTCFullYear() : null;
            const res = await previewPriorityOpeningBalance(openingBalanceFile, month, year);
            const entries = res?.openingBalanceEntries ?? [];
            const rows = res?.parsedRows ?? 0;

            setOpeningBalanceEntries(entries);
            setParsedRows(rows);
            setPreviewDone(true);
        } catch (err) {
            console.error('Priority opening balance preview failed:', err);
            setPreviewDone(false);
        } finally {
            setProcessing(false);
        }
    };

    const handleCommit = async () => {
        if (!openingBalanceFile) return;

        try {
            setProcessing(true);
            const month = selectedDate ? selectedDate.getUTCMonth() + 1 : null; // JavaScript months are 0-based
            const year = selectedDate ? selectedDate.getUTCFullYear() : null;
            const res = await commitPriorityOpeningBalance(openingBalanceFile, month, year);

            const errorCount = res?.errorCount ?? res?.[1] ?? 0;
            const errorMessages = res?.errorMessages ?? res?.[2] ?? [];

            if (errorCount > 0 && errorMessages.length > 0) {
                console.error('Import errors:', errorMessages);
            } else {
                handleClose();
            }
        } catch (err) {
            console.error('Priority opening balance commit error:', err);
        } finally {
            setProcessing(false);
        }
    };

    const handleClose = () => {
        setOpeningBalanceFile(null);
        setPreviewDone(false);
        setOpeningBalanceEntries([]);
        setParsedRows(0);
        setSelectedDate(new Date(Date.UTC(new Date().getFullYear(), new Date().getMonth(), 1)));
        onClose();
    };

    const formatAmount = (amount) => {
        if (amount == null) return '';
        return new Intl.NumberFormat('he-IL', { 
            minimumFractionDigits: 2, 
            maximumFractionDigits: 2 
        }).format(amount);
    };

    const formatSortCode = (sortCode) => {
        if (!sortCode) return '';
        if (sortCode === '1') return 'חובה';
        if (sortCode === '2') return 'זכות';
        return sortCode;
    };

    // Calculate totals
    const calculateTotals = () => {
        let totalDebit = 0;
        let totalCredit = 0;

        openingBalanceEntries.forEach(entry => {
            const amount = parseFloat(entry.amount) || 0;
            if (entry.sortCode === '1') {
                totalDebit += amount;
            } else if (entry.sortCode === '2') {
                totalCredit += Math.abs(amount);
            }
        });

        return {
            totalDebit,
            totalCredit,
            total: totalDebit - totalCredit
        };
    };

    const totals = previewDone ? calculateTotals() : { totalDebit: 0, totalCredit: 0, total: 0 };

    if (!open) return null;

    return (
        <div className="download-modal-overlay" onClick={handleClose}>
            <div className="download-modal" onClick={e => e.stopPropagation()}>
                <div className={`download-modal-content ${direction}`} style={{ position: 'relative', maxWidth: previewDone ? '1200px' : '600px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                        <h4 className="report-title">
                            {t('ImportPriorityOpeningBalance') || 'Priority - יתרת פתיחה'}
                        </h4>
                        <IconButton 
                            className="client-back-icon" 
                            onClick={handleClose}
                            sx={{ padding: '4px' }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                        <div>
                            <h5>{t('UploadFile')} (.xlsx/.xls)</h5>
                            <p style={{ fontSize: '12px', color: '#666', marginBottom: '10px' }}>
                                {t('PriorityOpeningBalanceFormat') || 'קובץ Excel עם עמודות: חשבון, תאור חשבון, סעיף, יתרה'}
                            </p>
                            <DropzoneArea
                                key={openingBalanceFile ? openingBalanceFile.name : 'priority-opening-balance-empty'}
                                onDrop={handleFileDrop}
                                accept=".xls,.xlsx"
                                filesLimit={1}
                            />
                            {openingBalanceFile && (
                                <div style={{ marginTop: 8 }}>
                                    <span>{t('FileSelected')}: {openingBalanceFile.name}</span>
                                </div>
                            )}
                        </div>
                        <div>
                            <h5>{t('SelectMonthYear') || 'בחר חודש ושנה'}</h5>
                            <CustomMonthDatePicker
                                date={selectedDate}
                                onDateChange={setSelectedDate}
                                label={t('MonthYear') || 'חודש ושנה'}
                                height="48px"
                                width="100%"
                            />
                        </div>
                    </div>

                    <div style={{ display: 'flex', gap: 15, marginTop: 20 }}>
                        <Button
                            variant="contained"
                            onClick={handlePreview}
                            disabled={!openingBalanceFile || processing}
                        >
                            {t('Preview')}
                        </Button>
                        <Button
                            variant="contained"
                            color="success"
                            disabled={!previewDone || processing}
                            onClick={handleCommit}
                        >
                            {processing ? t('Processing') : t('Import')}
                        </Button>
                    </div>

                    {previewDone && (
                        <div style={{ marginTop: 25, maxHeight: 800, overflow: 'auto' }}>
                            <div style={{ marginBottom: 15 }}>
                                <p style={{ fontSize: '14px', color: '#666' }}>
                                    {t('ParsedRows') || 'שורות שנקראו'}: <strong>{parsedRows}</strong> | 
                                    {' '}{t('OpeningBalanceEntries') || 'יתרות פתיחה'}: <strong>{openingBalanceEntries.length}</strong>
                                </p>
                            </div>

                            {/* Summary Section */}
                            {openingBalanceEntries.length > 0 && (
                                <div style={{ 
                                    marginBottom: 20, 
                                    padding: '15px', 
                                    backgroundColor: '#f5f5f5', 
                                    borderRadius: '8px',
                                    border: '1px solid #ddd'
                                }}>
                                    <h5 style={{ marginTop: 0, marginBottom: 15 }}>{t('Summary') || 'סיכום'}</h5>
                                    <div style={{ display: 'flex', gap: '30px', flexWrap: 'wrap' }}>
                                        <div style={{ flex: 1, minWidth: '150px' }}>
                                            <div style={{ fontSize: '13px', color: '#666', marginBottom: '5px' }}>
                                                {t('TotalDebit') || 'סה"כ חובה'}
                                            </div>
                                            <div style={{ 
                                                fontSize: '18px', 
                                                fontWeight: 'bold', 
                                                color: totals.totalDebit < 0 ? '#d32f2f' : totals.totalDebit > 0 ? '#388e3c' : '#666'
                                            }}>
                                                ₪{formatAmount(totals.totalDebit)}
                                            </div>
                                        </div>
                                        <div style={{ flex: 1, minWidth: '150px' }}>
                                            <div style={{ fontSize: '13px', color: '#666', marginBottom: '5px' }}>
                                                {t('TotalCredit') || 'סה"כ זכות'}
                                            </div>
                                            <div style={{ 
                                                fontSize: '18px', 
                                                fontWeight: 'bold', 
                                                color: totals.totalCredit < 0 ? '#d32f2f' : totals.totalCredit > 0 ? '#388e3c' : '#666'
                                            }}>
                                                ₪{formatAmount(totals.totalCredit)}
                                            </div>
                                        </div>
                                        <div style={{ flex: 1, minWidth: '150px' }}>
                                            <div style={{ fontSize: '13px', color: '#666', marginBottom: '5px' }}>
                                                {t('Total') || 'סה"כ כולל'}
                                            </div>
                                            <div style={{ 
                                                fontSize: '18px', 
                                                fontWeight: 'bold', 
                                                color: totals.total < 0 ? '#d32f2f' : totals.total > 0 ? '#388e3c' : '#666'
                                            }}>
                                                ₪{formatAmount(totals.total)}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                            
                            {openingBalanceEntries.length === 0 ? (
                                <p style={{ color: '#666' }}>{t('NoOpeningBalanceEntries') || 'לא נמצאו יתרות פתיחה'}</p>
                            ) : (
                                <>
                                    <h5>{t('OpeningBalanceEntries') || 'יתרות פתיחה'}</h5>
                                    <table className="sortcode-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                                        <thead>
                                            <tr style={{ backgroundColor: '#f5f5f5' }}>
                                                <th style={{ padding: '10px', textAlign: direction === 'rtl' ? 'right' : 'left', border: '1px solid #ddd' }}>{t('CardId') || 'מזהה כרטיס'}</th>
                                                <th style={{ padding: '10px', textAlign: direction === 'rtl' ? 'right' : 'left', border: '1px solid #ddd' }}>{t('CardName') || 'שם הכרטיס'}</th>
                                                <th style={{ padding: '10px', textAlign: direction === 'rtl' ? 'right' : 'left', border: '1px solid #ddd' }}>{t('PalCode') || 'חשבון ראשי'}</th>
                                                <th style={{ padding: '10px', textAlign: direction === 'rtl' ? 'right' : 'left', border: '1px solid #ddd' }}>{t('SortCode') || 'קוד מיון'}</th>
                                                <th style={{ padding: '10px', textAlign: 'right', border: '1px solid #ddd' }}>{t('Amount') || 'סכום'}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {openingBalanceEntries.map((entry, i) => {
                                                const isCredit = entry.sortCode === '2';
                                                const displayAmount = isCredit ? -(parseFloat(entry.amount) || 0) : (parseFloat(entry.amount) || 0);
                                                
                                                return (
                                                    <tr key={i} style={{ backgroundColor: i % 2 === 0 ? '#fff' : '#f9f9f9' }}>
                                                        <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: direction === 'rtl' ? 'right' : 'left' }}>{entry.cardId || ''}</td>
                                                        <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: direction === 'rtl' ? 'right' : 'left' }}>{entry.cardName || ''}</td>
                                                        <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: direction === 'rtl' ? 'right' : 'left' }}>{entry.palCode || ''}</td>
                                                        <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: direction === 'rtl' ? 'right' : 'left' }}>{formatSortCode(entry.sortCode)}</td>
                                                        <td style={{ 
                                                            padding: '10px', 
                                                            border: '1px solid #ddd', 
                                                            textAlign: 'right',
                                                            color: isCredit ? '#d32f2f' : 'inherit',
                                                            fontWeight: isCredit ? 'bold' : 'normal'
                                                        }}>
                                                            {formatAmount(displayAmount)}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PriorityOpeningBalanceImportModal;
